import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { VerticalPackSelector } from './VerticalPackSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';

interface TenantPackInfo {
  vertical_pack: string;
  is_custom: boolean;
  pack_name?: string;
  pack_description?: string;
  template_count: number;
  sequence_count: number;
}

interface VerticalPackManagerProps {
  compact?: boolean; // If true, show compact view for Settings page
}

export const VerticalPackManager: React.FC<VerticalPackManagerProps> = ({ compact = false }) => {
  const [packInfo, setPackInfo] = useState<TenantPackInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [showSelector, setShowSelector] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPackInfo();
  }, []);

  const loadPackInfo = async () => {
    try {
      setLoading(true);
      const response = await api.verticalPacks.getTenantInfo();
      const info = response.data;
      setPackInfo(info);
      setSelectedIndustry(info.vertical_pack);
    } catch (err: any) {
      console.error('Failed to load pack info:', err);
      setError('Failed to load vertical pack information');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPackClick = () => {
    if (!selectedIndustry) {
      return;
    }
    
    // If selecting the same pack, offer re-apply option
    if (selectedIndustry === packInfo?.vertical_pack) {
      setUpdateExisting(true);
      setShowConfirmDialog(true);
      return;
    }
    
    // For different pack, show normal confirmation
    setUpdateExisting(false);
    setShowConfirmDialog(true);
  };

  const handleApplyPack = async () => {
    if (!selectedIndustry) {
      return;
    }

    setShowConfirmDialog(false);

    try {
      setApplying(true);
      setError(null);
      setSuccess(null);

      const response = await api.verticalPacks.apply(selectedIndustry, updateExisting);
      const result = response.data;

      // Build success message with created/updated counts
      const parts: string[] = [];
      if (result.templates_created > 0) {
        parts.push(`created ${result.templates_created} template${result.templates_created !== 1 ? 's' : ''}`);
      }
      if (result.templates_updated > 0) {
        parts.push(`updated ${result.templates_updated} template${result.templates_updated !== 1 ? 's' : ''}`);
      }
      if (result.sequences_created > 0) {
        parts.push(`created ${result.sequences_created} sequence${result.sequences_created !== 1 ? 's' : ''}`);
      }
      if (result.sequences_updated > 0) {
        parts.push(`updated ${result.sequences_updated} sequence${result.sequences_updated !== 1 ? 's' : ''}`);
      }
      
      const action = updateExisting ? 're-applied' : 'applied';
      const message = parts.length > 0 
        ? `Successfully ${action} ${selectedIndustry} pack! ${parts.join(', ')}.`
        : `Successfully ${action} ${selectedIndustry} pack!`;

      setSuccess(message);
      setShowSelector(false);
      setUpdateExisting(false);
      
      // Reload pack info
      await loadPackInfo();
    } catch (err: any) {
      console.error('Failed to apply pack:', err);
      setError(err.response?.data?.detail || 'Failed to apply vertical pack');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={compact ? "" : "max-w-5xl mx-auto p-6"}>
      {!compact && (
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Vertical Pack Settings</h2>
          <p className="text-gray-600 mt-1">
            Manage industry-specific templates and configurations for your tenant
          </p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-green-600 mt-0.5 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-red-600 mt-0.5 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Current Pack Info */}
      {packInfo && !showSelector && (
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Current Pack</h3>
              {packInfo.is_custom ? (
                <p className="text-gray-600 mt-1">General / Custom</p>
              ) : (
                <>
                  <p className="text-gray-900 font-medium mt-1">{packInfo.pack_name}</p>
                  <p className="text-gray-600 text-sm">{packInfo.pack_description}</p>
                </>
              )}
              <div className="mt-4 flex items-center space-x-4 text-sm text-gray-600">
                <span>{packInfo.template_count} templates</span>
                <span>{packInfo.sequence_count} sequences</span>
              </div>
            </div>
            <div className="flex gap-2">
              {!packInfo.is_custom && (
                <button
                  onClick={() => {
                    setSelectedIndustry(packInfo.vertical_pack);
                    setUpdateExisting(true);
                    setShowConfirmDialog(true);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Re-apply Pack
                </button>
              )}
              <button
                onClick={() => setShowSelector(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Change Pack
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vertical Pack Selector */}
      {showSelector && (
        <div className="bg-white border rounded-lg p-6">
          <VerticalPackSelector
            selectedIndustry={selectedIndustry}
            onSelect={setSelectedIndustry}
            showPreview={true}
          />

          <div className="mt-6 flex items-center justify-end space-x-3">
            <button
              onClick={() => {
                setShowSelector(false);
                setSelectedIndustry(packInfo?.vertical_pack || 'general');
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={applying}
            >
              Cancel
            </button>
            <button
              onClick={handleApplyPackClick}
              disabled={applying || !selectedIndustry}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applying ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Applying...
                </span>
              ) : (
                'Apply Pack'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Warning about applying packs */}
      {showSelector && (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-yellow-600 mt-0.5 mr-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-yellow-800 font-medium">Note</p>
              <p className="text-yellow-700 text-sm mt-1">
                Applying a vertical pack will create new templates and sequences. Your existing
                content will not be affected.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>
              {updateExisting ? 'Re-apply Pack' : 'Confirm Pack Change'}
            </DialogTitle>
            <DialogDescription>
              {updateExisting 
                ? `Are you sure you want to re-apply the ${selectedIndustry} vertical pack? This will update existing templates and sequences.`
                : `Are you sure you want to apply the ${selectedIndustry} vertical pack?`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-medium mb-2">What will happen:</p>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                {updateExisting ? (
                  <>
                    <li>Existing templates and sequences with matching names will be updated</li>
                    <li>New templates and sequences (if any) will be created</li>
                    <li>Rating page copy will be updated with industry-specific messages</li>
                    <li>Your customizations to existing content will be overwritten</li>
                  </>
                ) : (
                  <>
                    <li>New templates and sequences will be created</li>
                    <li>Existing templates and sequences with the same names will be skipped (no duplicates)</li>
                    <li>Rating page copy will be updated with industry-specific messages</li>
                    <li>You can customize or delete any created content later</li>
                  </>
                )}
              </ul>
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfirmDialog(false);
                  setUpdateExisting(false);
                }}
                disabled={applying}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApplyPack}
                disabled={applying}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {applying ? 'Applying...' : updateExisting ? 'Yes, Re-apply Pack' : 'Yes, Apply Pack'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerticalPackManager;
